import React, { useMemo, useState } from 'react'
import moment from 'moment'
import classnames from 'classnames'
import PropTypes from 'prop-types'

import {
  AppleIcon,
  ChromeIcon,
  ConnectIcon,
  DesktopIcon,
  DeviceCard,
  EventTag,
  HostTile,
  WaveIcon,
  WristwatchIcon,
} from 'components'
import EventTags from 'components/EventCard/EventTags'
import AddressIcon from 'components/icons/AddressIcon'
import TimeIcon from 'components/icons/TimeIcon'

const TEXT_CONTEXT_CHAR_LENGTH = 300
const SMALL_GREY_TEXT = 'secondary-text text-white opacity-50'

const Separator = ({ className }) => (
  <div
    className={classnames(
      'border border-solid border-white border-opacity-30 h-0.5 w-full',
      className
    )}
  />
)

const MORE_INFO_CONTENT = [
  {
    label: 'Teleport Code of Conduct',
    body: 'Teleport is no different than dating in real life. This is an experience for love and positivity - negativity and indecent behaviour will result in a lifetime ban',
  },
  {
    label: 'Cancellation Policy',
    body: 'Tickets may be refunded up to 24 hours until the start of the event',
  },
]

const newEventView = true

const SmallTextContent = ({ label, body }) => (
  <>
    {label && <h5 className={classnames(SMALL_GREY_TEXT, 'tracking-tighter')}>{label}</h5>}
    <p
      className={classnames(SMALL_GREY_TEXT, 'w-full md:w-4/6 mb-4 tracking-tighter')}
      dangerouslySetInnerHTML={{ __html: body }}
    />
  </>
)

const Subtitle = ({ text, marginClass = '' }) => (
  <h3 className={classnames('text-white tracking-tighter uppercase font-semibold', marginClass)}>
    {text}
  </h3>
)

const EventInfo = ({ event }) => {
  const { location, description, title, host, tagsByType, format, startTime, address } = event
  const [showFullText, setShowFullText] = useState(false)

  const time = useMemo(() => `${moment(startTime).format('h:mm A')} to late`, [startTime])

  return (
    <div className="relative w-full px-4 lg:pr-5 lg:w-3/5 xl:w-8/12 xl:pr-12">
      <div className="absolute -top-14">
        <EventTags location={location} tagsByType={tagsByType} />
      </div>
      <div className="flex flex-col mb-10">
        <h2 className="font-medium text-4xl tracking-tight text-white uppercase">{title}</h2>
      </div>
      <div className="flex flex-col  mb-12 lg:items-center lg:flex-row">
        {newEventView ? (
          <>
            <EventTag className="mr-8" Icon={AddressIcon} text={address} />
            <EventTag Icon={TimeIcon} text={time} />
          </>
        ) : (
          <>
            <EventTag Icon={WaveIcon} text={`Up to ${format.rounds} dates`} />
            <EventTag
              Icon={WristwatchIcon}
              text={`${getRoundLengthLabel(format.roundLength)} each`}
            />
            <EventTag Icon={DesktopIcon} text="iOS or Desktop (Chrome)" />
            <EventTag Icon={ConnectIcon} text="Message your mutual connections" />
          </>
        )}
      </div>
      {description && (
        <>
          <Separator className="mb-8" />
          <div>
            <Subtitle text="About This Event" />
            <div className="mb-8">
              <SmallTextContent
                body={
                  !showFullText && description.length > TEXT_CONTEXT_CHAR_LENGTH
                    ? `${description.substring(0, TEXT_CONTEXT_CHAR_LENGTH)}...`
                    : description
                }
              />
              <u
                onClick={() => setShowFullText(!showFullText)}
                className="cursor-pointer font-normal text-white xl:w-3/5 lg:w-4/5 w-full m-0 ml-0 mb-8 sm:text-sm md:text-base tracking-tighter"
              >
                {description.length > TEXT_CONTEXT_CHAR_LENGTH
                  ? !showFullText
                    ? 'read more'
                    : 'read less'
                  : null}
              </u>
            </div>
          </div>
        </>
      )}
      {!newEventView && (
        <>
          <div>
            <Subtitle text="How To Join" marginClass="mb-4" />
            <div className="flex items-stretch mb-8 md:mb-18 flex-row mt-0">
              <DeviceCard
                Icon={AppleIcon}
                text="Teleport IOS app on the app store"
                iconBg="linear-gradient(139.99deg, #294356 -11.18%, rgba(207, 224, 226, 100) 103.57%)"
              />
              <DeviceCard
                Icon={ChromeIcon}
                text="Chrome on any computer"
                iconBg="linear-gradient(139.99deg, #D3A500 -11.18%, rgba(207, 224, 226, 100) 103.57%)"
              />
            </div>
          </div>
          {host && (
            <>
              <Separator className="mb-8" />
              <div>
                <Subtitle text="Meet Your Host" />
                <div>
                  <HostTile {...host} />
                </div>
              </div>
            </>
          )}
          <Separator className="my-8 md:my-12" />
          <div>
            <Subtitle text="More Information" />
            {MORE_INFO_CONTENT.map(({ label, body }, index) => (
              <SmallTextContent label={label} body={body} key={index} />
            ))}
          </div>
        </>
      )}
      <Separator className="mt-8" />
    </div>
  )
}

const getRoundLengthLabel = length => {
  let label = ''
  const minutes = Math.floor(length / 60)
  if (minutes > 0) {
    label = `${minutes} minutes`
  }
  const seconds = length % 60
  if (seconds > 0) {
    label += `${label.length > 0 ? ' and ' : ' '}${seconds} seconds`
  }
  return label
}

EventInfo.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    status: PropTypes.string,
    location: PropTypes.string,
    image: PropTypes.string,
  }),
}

export default EventInfo
